import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import {
    Bell,
    X,
    Trophy,
    CheckCircle,
    UserPlus,
    TrendingUp,
    Banknote,
    Users,
    Target,
    XCircle,
    Clock,
    AlertTriangle,
    AlertCircle,
    Send,
    Filter,
    FileText,
    Zap,
    Trash2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const ICON_MAP = {
    'trophy': Trophy,
    'check-circle': CheckCircle,
    'user-plus': UserPlus,
    'trending-up': TrendingUp,
    'banknote': Banknote,
    'users': Users,
    'target': Target,
    'x-circle': XCircle,
    'clock': Clock,
    'alert-triangle': AlertTriangle,
    'alert-circle': AlertCircle,
    'bell': Bell,
    'send': Send,
    'file-text': FileText,
    'zap': Zap,
};

const COLOR_STYLES = {
    success: {
        bg: 'bg-emerald-100',
        icon: 'text-emerald-600',
        border: 'border-l-emerald-500'
    },
    gold: {
        bg: 'bg-yellow-100',
        icon: 'text-yellow-600',
        border: 'border-l-yellow-500'
    },
    error: {
        bg: 'bg-red-100',
        icon: 'text-red-600',
        border: 'border-l-red-500'
    },
    warning: {
        bg: 'bg-amber-100',
        icon: 'text-amber-600',
        border: 'border-l-amber-500'
    },
    neutral: {
        bg: 'bg-slate-100',
        icon: 'text-slate-600',
        border: 'border-l-slate-500'
    }
};

// Types de filtres
const NOTIFICATION_FILTERS = [
    { value: 'all', label: 'Toutes', icon: Bell },
    { value: 'prospect', label: 'Prospects', icon: UserPlus },
    { value: 'commission', label: 'Commissions', icon: Banknote },
    { value: 'pipeline', label: 'Pipeline', icon: TrendingUp },
    { value: 'dossier', label: 'Dossiers', icon: FileText },
];

// Mapping type -> catégorie
const TYPE_TO_CATEGORY = {
    'new_prospect': 'prospect',
    'prospect_signed': 'prospect',
    'prospect_lost': 'prospect',
    'prospect_stale': 'prospect',
    'stage_advanced': 'pipeline',
    'dossier_validated': 'dossier',
    'dossier_transmis': 'dossier',
    'commission_earned': 'commission',
    'commission_validated': 'commission',
};

export const NotificationCenter = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [overdueCount, setOverdueCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        if (user) {
            loadNotifications();
            loadOverdueCount();
            // Poll for new notifications every 30 seconds
            const interval = setInterval(() => { loadNotifications(); loadOverdueCount(); }, 30000);
            return () => clearInterval(interval);
        }
    }, [user]);

    const loadOverdueCount = async () => {
        try {
            const res = await api.get('/tasks/my/overdue-count');
            setOverdueCount(res.data?.count || 0);
        } catch (error) {
            // silencieux
        }
    };

    const loadNotifications = async () => {
        try {
            const res = await api.get('/notifications');
            setNotifications(res.data.notifications || []);
            setUnreadCount(res.data.unread_count || 0);
        } catch (error) {
            console.error('Error loading notifications:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/read`);
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const deleteNotification = async (notificationId, e) => {
        e.stopPropagation();
        try {
            await api.delete(`/notifications/${notificationId}`);
            const notification = notifications.find(n => n.id === notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            if (!notification?.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        // Marquer comme lu
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        // Naviguer vers la cible : link_url prioritaire (ex. mur du bon module), sinon fallback par type
        const dest = notification.link_url
            || (notification.target_type === 'prospect' && notification.target_id
                ? `/prospects/${notification.target_id}` : null)
            || (notification.target_type === 'idea' ? '/boite-a-idees' : null)
            || (notification.target_type === 'feed_post' || (notification.type || '').startsWith('feed')
                ? '/feed' : null);
        if (dest) {
            navigate(dest);
        }
        // Toujours fermer le panneau au clic (jamais d'effet "mort")
        setOpen(false);
    };

    // Filtrer les notifications
    const filteredNotifications = notifications.filter(n => {
        if (filter === 'all') return true;
        const category = TYPE_TO_CATEGORY[n.type] || 'other';
        return category === filter;
    });

    const renderNotification = (notification) => {
        const IconComponent = ICON_MAP[notification.icon] || Bell;
        const colorStyle = COLOR_STYLES[notification.color] || COLOR_STYLES.neutral;

        return (
            <div
                key={notification.id}
                className={cn(
                    "flex items-stretch border-l-4 rounded-r-lg transition-all duration-200",
                    "hover:shadow-md",
                    colorStyle.border,
                    notification.is_read ? "bg-white" : "bg-slate-50"
                )}
                data-testid={`notification-${notification.id}`}
            >
                <button
                    type="button"
                    onClick={() => handleNotificationClick(notification)}
                    className="flex flex-1 items-start gap-3 p-4 text-left min-w-0"
                    data-testid={`notification-open-${notification.id}`}
                >
                    <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                        colorStyle.bg
                    )}>
                        <IconComponent className={cn("w-5 h-5", colorStyle.icon)} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <p className={cn(
                                "font-medium text-sm",
                                notification.is_read ? "text-slate-600" : "text-slate-900"
                            )}>
                                {notification.title}
                            </p>
                            {!notification.is_read && (
                                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                            )}
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">
                            {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className="inline-flex items-center gap-1 rounded-md bg-[#0B1F3B]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#0B1F3B]">
                                <img src="/icons/icon-192x192.png" alt="" className="h-3 w-3 rounded-sm object-contain" />
                                Valnexo
                            </span>
                            <span className="text-xs text-slate-400">
                                {format(new Date(notification.created_at), 'dd MMM à HH:mm', { locale: fr })}
                            </span>
                        </div>
                    </div>
                </button>
                {/* Zone de suppression nettement séparée pour éviter tout clic accidentel */}
                <div className="flex items-center border-l border-slate-100 px-2">
                    <button
                        onClick={(e) => deleteNotification(notification.id, e)}
                        className="p-2.5 rounded-lg text-slate-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                        aria-label="Supprimer la notification"
                        title="Supprimer"
                        data-testid={`notification-delete-${notification.id}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    className="relative flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                    data-testid="notification-bell"
                >
                    <Bell className="w-5 h-5 text-slate-400 hover:text-white transition-colors" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse" data-testid="notif-unread-badge">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent 
                className="w-[420px] p-0 shadow-xl" 
                align="end"
                sideOffset={8}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={markAllAsRead}
                            className="text-xs text-blue-600 hover:text-blue-700"
                        >
                            Tout marquer comme lu
                        </Button>
                    )}
                </div>

                {/* Bandeau tâches en retard */}
                {overdueCount > 0 && (
                    <button
                        onClick={() => { setOpen(false); navigate('/tasks?status=overdue'); }}
                        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 border-b border-red-100 transition-colors"
                        data-testid="bell-overdue-banner"
                    >
                        <span className="flex items-center gap-2 text-sm font-medium text-red-700">
                            <AlertTriangle className="w-4 h-4" />
                            {overdueCount} tâche{overdueCount > 1 ? 's' : ''} en retard
                        </span>
                        <span className="text-xs text-red-600">Voir →</span>
                    </button>
                )}

                {/* Filtres */}
                <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-slate-400" />
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="h-8 w-[180px] text-xs">
                                <SelectValue placeholder="Filtrer par type" />
                            </SelectTrigger>
                            <SelectContent>
                                {NOTIFICATION_FILTERS.map(f => (
                                    <SelectItem key={f.value} value={f.value}>
                                        <div className="flex items-center gap-2">
                                            <f.icon className="w-3 h-3" />
                                            {f.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {filter !== 'all' && (
                            <Badge variant="secondary" className="text-xs">
                                {filteredNotifications.length}
                            </Badge>
                        )}
                    </div>
                </div>

                {/* Notifications List */}
                <ScrollArea className="h-[400px]">
                    {filteredNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <Bell className="w-8 h-8 text-slate-400" />
                            </div>
                            <p className="text-slate-500 text-center">
                                {filter === 'all' ? 'Aucune notification' : 'Aucune notification de ce type'}
                            </p>
                            <p className="text-sm text-slate-400 text-center mt-1">
                                Vous serez notifié des événements importants
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredNotifications.map(renderNotification)}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};
